const REGIONS = ['APJ', 'EMEA', 'NA', 'LATAM'];
const INGESTION_TYPES = ['Instant Run', 'One-time Schedule', 'Recurring Schedule', 'Unknown'];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeRegion = (region) => {
  const normalized = String(region || '').trim().toUpperCase();
  return REGIONS.find((knownRegion) => knownRegion.toUpperCase() === normalized) || String(region || '').trim();
};

const periodFromEntry = (entry) =>
  entry?.period || entry?.label || entry?.month || entry?.week || entry?.day || entry?.x || '';

const mapSeriesByRegion = (rawObject, valueFieldCandidates) => {
  const source = rawObject && typeof rawObject === 'object' ? rawObject : {};
  const regionEntries = Object.entries(source).filter(([, value]) => Array.isArray(value));

  if (!regionEntries.length) {
    return { xData: [], series: [] };
  }

  const xData = safeArray(regionEntries[0][1]).map((entry) => periodFromEntry(entry));
  const series = regionEntries.map(([region, entries]) => ({
    name: normalizeRegion(region),
    data: safeArray(entries).map((entry) => {
      const candidate = valueFieldCandidates.find((field) => entry?.[field] !== undefined);
      return toNumber(candidate ? entry[candidate] : 0);
    }),
  }));

  return { xData, series };
};

const mapRegionalOverview = (
  regionalTopNarratives,
  avgNarrativeRunsPerSE,
  regionalAvgNarrativeRunsPerSE
) => {
  const topNarratives = regionalTopNarratives && typeof regionalTopNarratives === 'object'
    ? regionalTopNarratives
    : {};
  const avgRunsLegacy = avgNarrativeRunsPerSE && typeof avgNarrativeRunsPerSE === 'object'
    ? avgNarrativeRunsPerSE
    : {};
  const avgRunsByRegion = regionalAvgNarrativeRunsPerSE && typeof regionalAvgNarrativeRunsPerSE === 'object'
    ? regionalAvgNarrativeRunsPerSE
    : {};

  const regions = REGIONS;

  return regions.reduce((acc, region) => {
    const narrativeSource = Array.isArray(topNarratives[region])
      ? topNarratives[region]
      : Array.isArray(topNarratives[region?.toLowerCase?.()])
        ? topNarratives[region.toLowerCase()]
        : [];

    const narrativeList = safeArray(narrativeSource)
      .map((item) => (typeof item === 'string' ? item : item?.narrative || item?.Narrative || ''))
      .filter(Boolean);

    const avgSource = avgRunsByRegion[region]
      ?? avgRunsByRegion[region.toLowerCase()]
      ?? avgRunsLegacy[region]
      ?? avgRunsLegacy[region.toLowerCase()];
    const avgValue = typeof avgSource === 'number'
      ? avgSource
      : toNumber(avgSource?.avgRunsPerSE ?? avgSource?.average ?? avgSource?.runs);

    acc[region] = {
      topNarratives: narrativeList.slice(0, 3),
      avgRunsPerSE: avgValue || 0,
    };
    return acc;
  }, {});
};

const mapTopSEsStacked = (topSEsGlobal) => {
  const seRows = safeArray(topSEsGlobal);
  return {
    names: seRows.map((item, index) => item?.name || item?.email || `SE ${index + 1}`),
    series: [
      {
        name: 'Runs on PET Tenants',
        data: seRows.map((item) => toNumber(item?.petTenantRuns)),
      },
      {
        name: 'Runs on Shared Tenants',
        data: seRows.map((item) => toNumber(item?.sharedTenantRuns)),
      },
      {
        name: 'Runs on Unknown Tenants',
        data: seRows.map((item) => toNumber(item?.unknownTenantRuns)),
      },
    ],
  };
};

const mapRunDistribution = (runDistributionByRegion) => {
  if (Array.isArray(runDistributionByRegion)) {
    const totalRuns = runDistributionByRegion.reduce((sum, item) => sum + toNumber(item?.runs), 0);
    return runDistributionByRegion.reduce((acc, item) => {
      const region = normalizeRegion(item?.region);
      const percentage = item?.percentage !== undefined
        ? toNumber(item.percentage)
        : (totalRuns ? Math.round((toNumber(item?.runs) / totalRuns) * 100) : 0);
      if (region) acc[region] = percentage;
      return acc;
    }, {});
  }

  if (runDistributionByRegion && typeof runDistributionByRegion === 'object') {
    return Object.entries(runDistributionByRegion).reduce((acc, [region, value]) => {
      acc[normalizeRegion(region)] = toNumber(value?.percentage ?? value);
      return acc;
    }, {});
  }

  return {};
};

const mapRegionalNarrativeBreakdown = (regionalNarrativeBreakdown) => {
  if (regionalNarrativeBreakdown?.xData && regionalNarrativeBreakdown?.series) {
    return regionalNarrativeBreakdown;
  }

  const regionMap = regionalNarrativeBreakdown && typeof regionalNarrativeBreakdown === 'object'
    ? regionalNarrativeBreakdown
    : {};

  const xData = Object.keys(regionMap).map(normalizeRegion).filter(Boolean);
  const narrativeSet = new Set();

  xData.forEach((region) => {
    safeArray(regionMap[region]).forEach((entry) => {
      const narrativeName = entry?.narrative || entry?.Narrative;
      if (narrativeName) narrativeSet.add(narrativeName);
    });
  });

  const narratives = Array.from(narrativeSet);
  const series = narratives.map((narrative) => ({
    name: narrative,
    type: 'bar',
    stack: 'total',
    data: xData.map((region) => {
      const match = safeArray(regionMap[region]).find(
        (entry) => (entry?.narrative || entry?.Narrative) === narrative
      );
      return toNumber(match?.runs ?? match?.Count ?? match?.value);
    }),
  }));

  const total = xData.map((region, idx) => ({
    region,
    value: series.reduce((sum, item) => sum + toNumber(item.data[idx]), 0),
  }));

  return { xData, series, total };
};

const mapRegionalIngestion = (regionalIngestionTypeOverview) => {
  const ingestionMap = regionalIngestionTypeOverview && typeof regionalIngestionTypeOverview === 'object'
    ? regionalIngestionTypeOverview
    : {};

  return Object.entries(ingestionMap).reduce((acc, [regionKey, regionValue]) => {
    const region = normalizeRegion(regionKey);
    const regionData = regionValue || {};

    let rows = [];
    if (Array.isArray(regionData)) {
      rows = regionData;
    } else if (Array.isArray(regionData.types)) {
      rows = regionData.types;
    } else if (typeof regionData === 'object') {
      rows = Object.entries(regionData).map(([name, value]) => ({ name, value }));
    }

    const counts = rows.map((row) => toNumber(row?.runs ?? row?.value ?? row?.count));
    const countSum = counts.reduce((sum, count) => sum + count, 0);

    const types = INGESTION_TYPES.map((typeName) => {
      const row = rows.find((item) => String(item?.name || item?.type || '').trim() === typeName);
      const explicitPercent = row?.percent ?? row?.percentage;
      const count = toNumber(row?.runs ?? row?.value ?? row?.count);
      const derivedPercent = countSum ? Math.round((count / countSum) * 100) : 0;
      return {
        name: typeName,
        percent: explicitPercent !== undefined ? toNumber(explicitPercent) : derivedPercent,
      };
    });

    acc[region] = {
      total: toNumber(regionData.total) || countSum,
      types,
    };
    return acc;
  }, {});
};

const mapTopNarrativesGlobally = (topNarrativesGlobally, regionalNarrativeBreakdown) => {
  const result = {
    Total: [],
    APJ: [],
    EMEA: [],
    NA: [],
    LATAM: [],
  };

  const mapRows = (rows) => safeArray(rows).map((item) => ({
    Narrative: item?.narrative || item?.Narrative || 'Unknown',
    Count: toNumber(item?.runs ?? item?.Count ?? item?.value),
  }));

  if (Array.isArray(topNarrativesGlobally)) {
    result.Total = mapRows(topNarrativesGlobally);
  } else if (topNarrativesGlobally && typeof topNarrativesGlobally === 'object') {
    Object.entries(topNarrativesGlobally).forEach(([region, items]) => {
      const normalizedRegion = normalizeRegion(region);
      if (!normalizedRegion) return;
      if (!result[normalizedRegion]) result[normalizedRegion] = [];
      result[normalizedRegion] = mapRows(items);
    });

    if (!result.Total.length) {
      const regionAggregate = Object.values(result)
        .filter((entries) => Array.isArray(entries) && entries.length > 0)
        .flat();

      if (regionAggregate.length) {
        const totalMap = regionAggregate.reduce((acc, item) => {
          acc[item.Narrative] = (acc[item.Narrative] || 0) + toNumber(item.Count);
          return acc;
        }, {});

        result.Total = Object.entries(totalMap)
          .map(([Narrative, Count]) => ({ Narrative, Count }))
          .sort((a, b) => b.Count - a.Count);
      }
    }
  }

  const regionalBreakdownSource = regionalNarrativeBreakdown && typeof regionalNarrativeBreakdown === 'object'
    ? regionalNarrativeBreakdown
    : {};

  REGIONS.forEach((region) => {
    if (result[region]?.length) return;
    result[region] = mapRows(regionalBreakdownSource[region]);
  });

  return result;
};

const mapRegionalSERunsBreakdown = (regionalSENarrativeRunsBreakdown) => {
  const source = regionalSENarrativeRunsBreakdown && typeof regionalSENarrativeRunsBreakdown === 'object'
    ? regionalSENarrativeRunsBreakdown
    : {};

  return Object.entries(source).reduce((acc, [regionKey, value]) => {
    const region = normalizeRegion(regionKey);
    const rows = Array.isArray(value)
      ? value
      : Object.entries(value || {}).map(([name, details]) => ({ name, ...(details || {}) }));

    const mappedUsers = rows.reduce((userAcc, entry, index) => {
      const userName = entry?.name || entry?.Name || `SE ${index + 1}`;
      const narratives = safeArray(entry?.narratives || entry?.Narratives).map((item) => ({
        Narrative: item?.narrative || item?.Narrative || 'Unknown',
        Count: toNumber(item?.runs ?? item?.Count ?? item?.value),
      }));

      userAcc[userName] = {
        Total: toNumber(entry?.totalRuns ?? entry?.Total) || narratives.reduce((sum, n) => sum + n.Count, 0),
        Narratives: narratives,
      };
      return userAcc;
    }, {});

    acc[region] = mappedUsers;
    return acc;
  }, {});
};

export const mapInternalSEAnalyticsResponse = (internalSEAnalytics = {}) => ({
  globalNarrativeTrend: safeArray(internalSEAnalytics.globalNarrativeVolumesAndTrends).map((item) => ({
    period: periodFromEntry(item),
    runs: toNumber(item?.runs),
  })),
  regionalOverview: mapRegionalOverview(
    internalSEAnalytics.regionalTopNarratives,
    internalSEAnalytics.avgNarrativeRunsPerSE,
    internalSEAnalytics.regionalAvgNarrativeRunsPerSE
  ),
  topSEsStacked: mapTopSEsStacked(internalSEAnalytics.topSEsGlobal),
  runDistributionByRegion: mapRunDistribution(internalSEAnalytics.runDistributionByRegion),
  narrativeTrendsPerRegion: mapSeriesByRegion(
    internalSEAnalytics.narrativeVolumesAndTrendsPerRegion,
    ['runs', 'value', 'count']
  ),
  regionalNarrativeBreakdown: mapRegionalNarrativeBreakdown(
    internalSEAnalytics.regionalNarrativeBreakdown
  ),
  regionalIngestion: mapRegionalIngestion(internalSEAnalytics.regionalIngestionTypeOverview),
  topNarrativesGlobally: mapTopNarrativesGlobally(
    internalSEAnalytics.topNarrativesGlobally,
    internalSEAnalytics.regionalNarrativeBreakdown
  ),
  bookingVolumePerRegion: mapSeriesByRegion(
    internalSEAnalytics.bookingVolumePerRegion,
    ['bookings', 'runs', 'value', 'count']
  ),
  regionalSENarrativeBreakdown: mapRegionalSERunsBreakdown(
    internalSEAnalytics.regionalSENarrativeRunsBreakdown
  ),
});

